/**
 * useHierarchicalApprove — shared hierarchical-mode primitive for v2
 * workflow steps that route through `approveHierarchicalData`.
 *
 * Stories 4-10 each surface a Current TARA / Child TARAs split when
 * the session's `analysis_mode === 'hierarchical'`. The pattern is
 * identical across steps — only the agent name + the primary-key
 * field on each record differ. This hook centralises:
 *
 *   - dispatching relevance + justification edits into the reducer
 *     (Story H2 — `state.steps[stepId].hierarchicalModifications`)
 *   - the arg-less `onApprove` closure that builds
 *     `approvedItems = newItems ∪ relevantChildren` and fires
 *     `approveHierarchicalData(threadId, agentName, approvedItems,
 *     modifications)` then dispatches `clearHierModifications` +
 *     triggers `onApproved` (typically `structured.refetch`).
 *
 * Story H2 — modifications now live in the reducer instead of local
 * `useState`. Switching steps and returning preserves the
 * in-progress map; only a successful approve clears it. Step 7/8's
 * bespoke `unifiedApprove` closures still use local useState (they
 * coalesce two collections per save, which the single-collection
 * shared hook can't express) — they'll migrate as a follow-up.
 *
 * Per the rule-of-three: extracted at the start of Story 5 to avoid
 * duplicating Step 2's pattern into Steps 3-8. See memory note
 * `project_epic12_hierarchical_refactor.md`.
 */
import { useCallback, useState } from 'react';
import { approveHierarchicalData } from '../../../api/workflow';
import { notify } from '../../../services/notificationBus';
import type {
  HierarchicalModifications,
  WorkflowActions,
  WorkflowV2State,
} from '../state/workflowV2Types';

export interface UseHierarchicalApproveInput {
  stepId: number;
  threadId: string | null | undefined;
  agentName: string;
  /** True iff session.analysis_mode === 'hierarchical'. */
  enabled: boolean;
  /** Current full record list (new + inherited). The hook re-reads
   *  this on every approve to compute `approvedItems`. */
  records: Array<Record<string, unknown>>;
  /** Primary-key field name (e.g. 'asset_id'). Used to scope
   *  modifications + dedupe child rows. */
  primaryKey: string;
  /** Reducer state — the hook reads
   *  `state.steps[stepId].hierarchicalModifications`. */
  state: WorkflowV2State;
  /** Action creators bound to the reducer's dispatch — the hook
   *  forwards setRelevance / setJustification / clearHierModifications
   *  through these. */
  actions: WorkflowActions;
  /** Returns true for records inherited from a child TARA.
   *  Default: `r.source === 'child_tara'`. */
  isChild?: (record: Record<string, unknown>) => boolean;
  /** Called after a successful approve. Typically `structured.refetch`. */
  onApproved?: () => void;
}

export interface UseHierarchicalApproveResult {
  modifications: HierarchicalModifications;
  setRelevance: (id: string, isRelevant: boolean) => void;
  setJustification: (id: string, justification: string) => void;
  onApprove: () => Promise<void>;
  approving: boolean;
  approveError: string | null;
}

const defaultIsChild = (r: Record<string, unknown>): boolean => r?.source === 'child_tara';

export function useHierarchicalApprove({
  stepId,
  threadId,
  agentName,
  enabled,
  records,
  primaryKey,
  state,
  actions,
  isChild = defaultIsChild,
  onApproved,
}: UseHierarchicalApproveInput): UseHierarchicalApproveResult {
  // Story H2 — modifications live in the reducer; only the
  // in-flight + last-error UI flags stay local.
  const modifications = state.steps[stepId]?.hierarchicalModifications ?? {};
  const [approving, setApproving] = useState<boolean>(false);
  const [approveError, setApproveError] = useState<string | null>(null);

  const setRelevance = useCallback(
    (id: string, isRelevant: boolean) => {
      actions.setHierRelevance(stepId, id, isRelevant);
    },
    [actions, stepId],
  );

  const setJustification = useCallback(
    (id: string, justification: string) => {
      actions.setHierJustification(stepId, id, justification);
    },
    [actions, stepId],
  );

  const onApprove = useCallback(async () => {
    if (!enabled) return;
    if (!threadId) throw new Error('No active workflow session');
    setApproving(true);
    setApproveError(null);
    try {
      const newItems = records.filter((r) => !isChild(r));
      const children = records.filter((r) => isChild(r));
      const relevant = children.filter((c) => {
        const id = String(c[primaryKey] ?? '');
        const override = modifications[id]?.is_relevant;
        if (override === undefined) return c.is_relevant !== false;
        return override !== false;
      });
      const approvedItems = [...newItems, ...relevant];
      await approveHierarchicalData(threadId, agentName, approvedItems, modifications);
      // Story H2 — clear the diff map on successful save so the
      // user starts fresh next time they re-enter edit mode.
      actions.clearHierModifications(stepId);
      onApproved?.();
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to approve relevance';
      const message = typeof msg === 'string' ? msg : 'Failed to approve relevance';
      setApproveError(message);
      // Story 15.F — surface save failures (Done button + global
      // Approve in hierarchical mode) via the shared toast bus. The
      // `useApproveFlow` toast only covers the `resumeWorkflow` half
      // of the chained approve, so hierarchical-only failures (the
      // save half) were previously silent.
      notify({ type: 'error', title: 'Save failed', message });
      throw e;
    } finally {
      setApproving(false);
    }
  }, [enabled, threadId, agentName, records, primaryKey, isChild, modifications, onApproved, actions, stepId]);

  return { modifications, setRelevance, setJustification, onApprove, approving, approveError };
}
